import { useState, useEffect } from "react";
import { Plus, Trash2, Edit2, ImageIcon, ExternalLink, Eye, EyeOff, Facebook, Instagram, MessageCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";

import { API } from "../../config/api";

const PLATFORMS = [
  { id: 'facebook', label: 'Facebook', icon: Facebook },
  { id: 'instagram', label: 'Instagram', icon: Instagram },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
];

export default function AdminPromos() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    image_url: "",
    text: "",
    button_link: "",
    button_text: "",
    platforms: [],
    is_active: true
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchPromos();
  }, []);

  const getToken = () => localStorage.getItem("admin_token");

  const fetchPromos = async () => {
    try {
      const response = await fetch(`${API}/admin/promos`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (response.ok) {
        setPromos(await response.json());
      }
    } catch (error) {
      toast.error("Failed to load promos");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      const response = await fetch(`${API}/admin/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: uploadData
      });

      if (response.ok) {
        const data = await response.json();
        const isProduction = window.location.hostname === 'tatvamasi.ca';
        const API_BASE = isProduction ? "" : (process.env.REACT_APP_BACKEND_URL || "");
        const fullUrl = `${API_BASE}${data.url}`;
        setFormData({ ...formData, image_url: fullUrl });
        toast.success("Image uploaded!");
      } else {
        toast.error("Upload failed");
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const togglePlatform = (platformId) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const resetForm = () => {
    setFormData({
      title: "",
      image_url: "",
      text: "",
      button_link: "",
      button_text: "",
      platforms: [],
      is_active: true
    });
    setEditingPromo(null);
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    setFormData({
      title: promo.title,
      image_url: promo.image_url,
      text: promo.text,
      button_link: promo.button_link || "",
      button_text: promo.button_text || "",
      platforms: promo.platforms || [],
      is_active: promo.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image_url) {
      toast.error("Please upload an image or provide a URL");
      return;
    }

    try {
      const url = editingPromo 
        ? `${API}/admin/promos/${editingPromo.id}`
        : `${API}/admin/promos`;
      
      const response = await fetch(url, {
        method: editingPromo ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          ...formData,
          order_index: editingPromo ? editingPromo.order_index : promos.length
        })
      });

      if (response.ok) {
        toast.success(editingPromo ? "Promo updated!" : "Promo created!");
        setDialogOpen(false);
        resetForm();
        fetchPromos();
      } else {
        toast.error("Failed to save promo");
      }
    } catch {
      toast.error("Failed to save promo");
    }
  };

  const handleDelete = async (promoId) => {
    if (!confirm("Are you sure you want to delete this promo?")) return;

    try {
      const response = await fetch(`${API}/admin/promos/${promoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      if (response.ok) {
        toast.success("Promo deleted");
        fetchPromos();
      } else {
        toast.error("Failed to delete promo");
      }
    } catch {
      toast.error("Failed to delete promo");
    }
  };

  const toggleActive = async (promo) => {
    try {
      const response = await fetch(`${API}/admin/promos/${promo.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ is_active: !promo.is_active })
      });

      if (response.ok) {
        toast.success(`Promo ${promo.is_active ? 'hidden' : 'visible'}`);
        fetchPromos();
      }
    } catch {
      toast.error("Failed to update promo");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-promos">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-heading text-2xl text-kerala-green">Social Media Promos</h1>
          <p className="text-gray-600">Create shareable promotional content for social media</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-promo-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Promo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPromo ? "Edit Promo" : "Create New Promo"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Annual Picnic 2026 - Book Now!"
                  required
                  data-testid="promo-title-input"
                />
              </div>

              <div>
                <Label>Image</Label>
                <div className="mt-2 space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="flex-1">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-kerala-green transition-colors">
                        <ImageIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">
                          {uploading ? "Uploading..." : "Click to upload image"}
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  </div>
                  
                  <div className="text-center text-sm text-gray-500">or</div>
                  
                  <Input
                    placeholder="Paste image URL"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    data-testid="promo-image-url-input"
                  />
                </div>

                {formData.image_url && (
                  <div className="mt-3">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="text">Promo Text</Label>
                <Textarea
                  id="text"
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  placeholder="Join us for a fun-filled day at the Annual Picnic 2026! Enjoy food, games, and community bonding..."
                  rows={4}
                  required
                  data-testid="promo-text-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="button_link">Link URL (optional)</Label>
                  <Input
                    id="button_link"
                    value={formData.button_link}
                    onChange={(e) => setFormData({ ...formData, button_link: e.target.value })}
                    placeholder="https://..."
                    data-testid="promo-link-input"
                  />
                </div>
                <div>
                  <Label htmlFor="button_text">Link Text (optional)</Label>
                  <Input
                    id="button_text"
                    value={formData.button_text}
                    onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                    placeholder="Book Now"
                    data-testid="promo-link-text-input"
                  />
                </div>
              </div>

              <div>
                <Label>Target Platforms</Label>
                <div className="flex gap-3 mt-2">
                  {PLATFORMS.map((platform) => (
                    <button
                      key={platform.id}
                      type="button"
                      onClick={() => togglePlatform(platform.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        formData.platforms.includes(platform.id)
                          ? 'bg-kerala-green text-white border-kerala-green'
                          : 'border-gray-300 hover:border-kerala-green'
                      }`}
                    >
                      <platform.icon className="w-4 h-4" />
                      {platform.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Visible to public</Label>
              </div>

              <Button type="submit" className="btn-primary w-full" data-testid="submit-promo-btn">
                {editingPromo ? "Update Promo" : "Create Promo"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Promos Grid */}
      {promos.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-lg">
          <ImageIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-600">No promos yet</p>
          <p className="text-gray-400 text-sm">Create promotional content for social media sharing</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {promos.map((promo) => (
            <div
              key={promo.id}
              className={`bg-white rounded-lg shadow overflow-hidden ${!promo.is_active ? 'opacity-60' : ''}`}
              data-testid={`promo-${promo.id}`}
            >
              <div className="aspect-video relative">
                <img
                  src={promo.image_url}
                  alt={promo.title}
                  className="w-full h-full object-cover"
                />
                {promo.platforms && promo.platforms.length > 0 && (
                  <div className="absolute top-2 right-2 flex gap-1">
                    {promo.platforms.map((platformId) => {
                      const platform = PLATFORMS.find(p => p.id === platformId);
                      return platform ? (
                        <span 
                          key={platformId}
                          className="w-6 h-6 bg-white/90 rounded-full flex items-center justify-center"
                        >
                          <platform.icon className="w-3 h-3 text-kerala-green" />
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
                {!promo.is_active && (
                  <div className="absolute top-2 left-2 bg-gray-900/80 text-white text-xs px-2 py-1 rounded">
                    Hidden
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-heading text-lg text-kerala-green mb-2 truncate">{promo.title}</h3>
                <p className="text-gray-600 text-sm line-clamp-2 mb-4">{promo.text}</p>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(promo)}
                    data-testid={`edit-promo-${promo.id}`}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleActive(promo)}
                    data-testid={`toggle-promo-${promo.id}`}
                  >
                    {promo.is_active ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        Hide
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        Show
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(promo.id)}
                    className="text-red-500 hover:text-red-700"
                    data-testid={`delete-promo-${promo.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
